<script setup lang="ts">
/**
 * Подъём стенда с экрана раздела: репозиторий, ветка и задача.
 *
 * Репозиторий и ветку выбирают из готовых списков (DEV-1589): оба известны
 * трекеру, а руками их набирали до первого отказа пайплайна — опечатку в имени
 * ветки больше ничем не поймать. Наверху списка репозиториев те, где сейчас
 * идёт работа: стенд поднимают для ветки, у которой уже открыт merge request.
 *
 * Задачу спрашиваем ключом и находим по нему: сервису нужен идентификатор, а
 * человек знает «DEV-1476». Ключ подставляется из имени ветки — по
 * договорённости ветка начинается с него, и переспрашивать то, что уже
 * написано, незачем.
 */
import { AppButton, AppModal, AppSelect, AppTextField } from '@vinprop/design-system'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { raiseStand } from '../api'
import { listBranches, listRepositoryPaths, type Branch } from '../gitlab'
import { getIssueByKey } from '@/features/issue/api'

const emit = defineEmits<{ done: []; close: [] }>()

const { t } = useI18n()

const projectPath = ref('')
const branch = ref('')
const issueKey = ref('')
const busy = ref(false)
const error = ref('')

const repositories = ref<string[]>([])
const branches = ref<Branch[]>([])
const isLoadingRepositories = ref(false)
const isLoadingBranches = ref(false)

const repositoryOptions = computed(() =>
  repositories.value.map((path) => ({ value: path, label: path })),
)

const branchOptions = computed(() =>
  branches.value.map((item) => ({
    value: item.name,
    label: item.name,
    // Ветка по умолчанию подписана: с неё стенд поднимают редко, и увидеть её
    // среди двух десятков одинаковых имён полезно.
    description: item.isDefault ? t('devops.envs.raise.defaultBranch') : '',
  })),
)

/** Ключ из имени ветки: `DEV-1476-devops` → `DEV-1476`. */
const keyFromBranch = (name: string) => name.trim().match(/^[A-Z]+-\d+/)?.[0] ?? ''

watch(branch, (value) => {
  const guessed = keyFromBranch(value)

  if (guessed) issueKey.value = guessed
})

// Смена репозитория обнуляет ветку: ветки чужого репозитория здесь не при чём.
watch(projectPath, async (path) => {
  branch.value = ''
  branches.value = []

  if (!path) return

  isLoadingBranches.value = true

  try {
    branches.value = await listBranches(path)
  } catch {
    // Без списка ветку не выбрать, но форма не должна из-за этого закрываться:
    // причина скажется отказом при отправке.
    branches.value = []
  } finally {
    isLoadingBranches.value = false
  }
})

onMounted(async () => {
  isLoadingRepositories.value = true

  try {
    repositories.value = await listRepositoryPaths()
  } catch {
    repositories.value = []
  } finally {
    isLoadingRepositories.value = false
  }
})

const isValid = computed(
  () => projectPath.value.trim() && branch.value.trim() && issueKey.value.trim(),
)

const submit = async () => {
  if (!isValid.value) return

  busy.value = true
  error.value = ''

  try {
    const issue = await getIssueByKey(issueKey.value.trim())

    if (!issue) {
      error.value = t('devops.envs.raise.unknownIssue')

      return
    }

    await raiseStand(issue.id, projectPath.value.trim(), branch.value.trim())
    emit('done')
  } catch {
    error.value = t('devops.envs.actionError')
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <AppModal
    model-value
    :title="t('devops.envs.raise.title')"
    size="small"
    @update:model-value="emit('close')"
  >
    <div class="raise">
      <p class="raise__hint">{{ t('devops.envs.raise.hint') }}</p>

      <AppSelect
        v-model="projectPath"
        class="raise__repo"
        :options="repositoryOptions"
        :label="t('devops.envs.raise.service')"
        :placeholder="t('devops.envs.raise.servicePlaceholder')"
        :empty-text="t('devops.envs.raise.noRepositories')"
        :loading="isLoadingRepositories"
        searchable
      />

      <AppSelect
        v-model="branch"
        class="raise__branch"
        :options="branchOptions"
        :label="t('devops.envs.raise.branch')"
        :placeholder="t('devops.envs.raise.branchPlaceholder')"
        :empty-text="
          projectPath ? t('devops.envs.raise.noBranches') : t('devops.envs.raise.pickRepository')
        "
        :loading="isLoadingBranches"
        :disabled="!projectPath"
        searchable
      />

      <AppTextField
        v-model="issueKey"
        class="raise__issue"
        :label="t('devops.envs.raise.issue')"
        placeholder="DEV-1476"
        :error="error"
      />
    </div>

    <template #footer>
      <AppButton variant="secondary" @click="emit('close')">
        {{ t('common.actions.cancel') }}
      </AppButton>
      <AppButton :loading="busy" :disabled="!isValid" @click="submit">
        {{ t('devops.stand.raise') }}
      </AppButton>
    </template>
  </AppModal>
</template>

<style scoped>
.raise {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.raise__hint {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-body-sm);
}
</style>
